import { createSlice } from '@reduxjs/toolkit';
import {
  handleSetSelectedFilterIds,
  handleSetSelectedContentBasedValues,
  handleSetSelectedFilterValue,
} from 'store/helpers/filterHelpers';
import {
  fetchBlackList,
  fetchBlackListResources,
  searchResources,
  saveBlackListResources,
  removeFromBlacklist,
} from './actions';
import {
  instagramInitialState,
  telegramInitialState,
  twitterInitialState,
  newsInitialState,
  newspaperInitialState,
  nativePlatformsInitialState,
} from './initialState';

const filtersInitialState = {
  instagram: instagramInitialState,
  telegram: telegramInitialState,
  twitter: twitterInitialState,
  news: newsInitialState,
  newspaper: newspaperInitialState,
  nativePlatforms: nativePlatformsInitialState,
};

const initialState = {
  title: '',
  method: 'post',
  description: '',
  addedResources: [],
  selectedPlatformForResources: 'news',
  selectedBlacklistIds: [],
  filters: filtersInitialState,
  searchResources: {
    page: 1,
    data: [],
    loading: false,
    error: null,
    total: null,
  },
  blackList: {
    data: [],
    loading: false,
    error: null,
  },
  removeFromBlacklist: {
    loading: false,
    error: null,
  },
  resources: {
    page: 1,
    data: [],
    loading: false,
    error: null,
    total: null,
  },
  ids: {},
  blackListUpdateStatus: {
    loading: false,
    error: null,
  },
};

export const blackListSlice = createSlice({
  name: 'blackListSlice',
  initialState: initialState,
  reducers: {
    changeBlackListvalue: (state, action) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    setSelectedBlacklistIds: (state, action) => {
      state.selectedBlacklistIds = action.payload;
    },
    changeBlackListSearchPage: (state, action) => {
      state.searchResources.page = action?.payload;
    },
    addResourceToSelectedResources: (state, action) => {
      state.addedResources.push(action.payload);
    },
    deleteResourceFromSelectedResources: (state, action) => {
      state.addedResources = state.addedResources.filter(
        (resource) => resource.id !== action.payload
      );
    },
    setSelectedFilterIds: (state, action) => {
      handleSetSelectedFilterIds(state, action);
    },
    setSelectedContentBasedValues: (state, action) => {
      handleSetSelectedContentBasedValues(state, action);
    },
    setSelectedFilterValue: (state, action) => {
      handleSetSelectedFilterValue(state, action, filtersInitialState);
    },
    changeSortBy: (state, action) => {
      const { componentName, parentPlatform, order, selected } = action.payload;
      const sort = state.filters[parentPlatform][componentName];
      if (order) {
        sort.order = order;
      }
      if (selected) {
        sort.selected = selected;
      }
    },
    resetAllFilters: (state) => {
      state.filters = filtersInitialState;
    },
    setSelectedPlatformForResources: (state, action) => {
      state.selectedPlatformForResources = action.payload;
    },
    toggleFilterActivateSwitch: (state, action) => {
      const { componentName } = action.payload;
      const filter =
        state.filters[state.selectedPlatformForResources][componentName];
      filter.isDisabled = !filter.isDisabled;
    },
    addToIds: (state, action) => {
      state.ids[action.payload] = action.payload;
    },
    changePageNumber: (state, action) => {
      state.resources.page = action.payload;
    },
    removeFromIds: (state, action) => {
      delete state.ids[action.payload];
    },
    resetAddBlacklistModalState: (state) => {
      state.method = 'post';
      state.addedResources = [];
      state.searchResources = initialState.searchResources;
      state.ids = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBlackList.fulfilled, (state, action) => {
        state.blackList.data = action.payload ?? [];
        state.blackList.loading = false;
        state.blackList.error = null;
      })
      .addCase(fetchBlackList.pending, (state) => {
        state.blackList.loading = true;
        state.blackList.error = null;
      })
      .addCase(fetchBlackList.rejected, (state, action) => {
        state.blackList.loading = false;
        state.blackList.error = action.payload;
      })
      .addCase(fetchBlackListResources.fulfilled, (state, action) => {
        const { onlyIds } = action.meta.arg;
        if (onlyIds) {
          state.ids = action.payload?.data?.reduce((acc, item) => {
            acc[item.id] = item.id;
            return acc;
          }, {});
          return;
        }
        state.resources.data = action.payload?.data ?? [];
        state.resources.total = action.payload?.total;
        state.resources.loading = false;
        state.resources.error = null;
      })
      .addCase(fetchBlackListResources.pending, (state, action) => {
        const { onlyIds } = action.meta.arg;
        if (onlyIds) {
          state.ids = {};
          return;
        }
        state.resources.loading = true;
        state.resources.error = null;
        state.resources.total = null;
      })
      .addCase(fetchBlackListResources.rejected, (state, action) => {
        const { onlyIds } = action.meta.arg;
        if (onlyIds) {
          state.ids = {};
          return;
        }
        state.resources.loading = false;
        state.resources.error = action.payload;
        state.resources.total = null;
      })
      .addCase(searchResources.fulfilled, (state, action) => {
        state.searchResources.data = action.payload?.data ?? [];
        state.searchResources.total = action.payload?.total;
        state.searchResources.loading = false;
        state.searchResources.error = null;
      })
      .addCase(searchResources.pending, (state) => {
        state.searchResources.data = [];
        state.searchResources.loading = true;
        state.searchResources.error = null;
      })
      .addCase(searchResources.rejected, (state, action) => {
        state.searchResources.loading = false;
        state.searchResources.error = action.payload;
        state.searchResources.total = null;
      })
      .addCase(saveBlackListResources.fulfilled, (state) => {
        state.blackListUpdateStatus.loading = false;
        state.blackListUpdateStatus.error = null;
      })
      .addCase(saveBlackListResources.pending, (state) => {
        state.blackListUpdateStatus.loading = true;
        state.blackListUpdateStatus.error = null;
      })
      .addCase(saveBlackListResources.rejected, (state, action) => {
        state.blackListUpdateStatus.loading = false;
        state.blackListUpdateStatus.error = action.payload;
      })
      .addCase(removeFromBlacklist.pending, (state) => {
        state.removeFromBlacklist.loading = true;
        state.removeFromBlacklist.error = null;
      })
      .addCase(removeFromBlacklist.rejected, (state, action) => {
        state.removeFromBlacklist.loading = false;
        state.removeFromBlacklist.error = action.payload;
      })
      .addCase(removeFromBlacklist.fulfilled, (state) => {
        state.removeFromBlacklist.loading = false;
        state.removeFromBlacklist.error = null;
      });
  },
});

export const {
  changeBlackListvalue,
  changeBlackListSearchPage,
  addResourceToSelectedResources,
  deleteResourceFromSelectedResources,
  resetAddBlacklistModalState,
  addToIds,
  removeFromIds,
  setSelectedPlatform,
  setSelectedFilterIds,
  setSelectedContentBasedValues,
  setSelectedFilterValue,
  changeSortBy,
  resetAllFilters,
  setSelectedPlatformForResources,
  toggleFilterActivateSwitch,
  setSelectedBlacklistIds,
  changePageNumber,
} = blackListSlice.actions;
